import { useParams, Navigate } from "react-router-dom";
import SeoLanding from "@/components/SeoLanding";
import { AlertOctagon, Clock, Gavel } from "lucide-react";
import { STATES } from "@/data/stateData";

/**
 * Dynamic state-specific SEO landing page.
 * Route: /stop-foreclosure-in/:state (slug lowercase, hyphenated)
 * Ranks for "stop foreclosure in {State}" and "sheriff sale help {State}".
 */
export default function StateForeclosureLanding() {
  const { state } = useParams();
  const data = STATES[state?.toLowerCase()];
  if (!data) return <Navigate to="/stop-foreclosure-fast" replace />;

  const countyList = data.counties.slice(0, 5).join(", ") + " and " + data.counties[data.counties.length - 1];

  return (
    <SeoLanding
      slug={`stop-foreclosure-in/${state}`}
      crumb={`Stop Foreclosure in ${data.name}`}
      eyebrow={`MMXXVI · ${data.abbrev} · Trustee Ledger`}
      pageTitle={`Stop Foreclosure in ${data.name} — 60-Day Counterclaim Help | Azure Gypsy Guild`}
      pageDescription={`Facing foreclosure in ${data.name}? Azure Gypsy Guild files a verified counterclaim inside your existing case, pledges trustee funds, and settles the mortgage in 60 days. 98% settlement rate. Emergency ${data.saleName.toLowerCase()} triage for ${data.counties[0]}, ${data.counties[1]}, ${data.counties[2]} and every ${data.abbrev} county.`}
      h1={`Stop foreclosure in ${data.name}. In sixty days.`}
      subhead={`${data.name} is a ${data.procedure.toLowerCase()} state. ${data.localHook} Azure Gypsy Guild files a verified counterclaim against the law firm and bank, pledges funds into the trustee account, and settles the mortgage through a good accounting ledger audit — before the ${data.saleName.toLowerCase()} closes.`}
      pillars={[
        { icon: AlertOctagon, value: data.abbrev, label: `${data.saleName}`, desc: data.saleDay },
        { icon: Clock,        value: `${data.noticeDays}d`, label: "Statutory notice",   desc: data.timeToSale + " to sale" },
        { icon: Gavel,        value: "98%", label: "Guild settlement rate", desc: `On qualified ${data.abbrev} counterclaims` },
      ]}
      sections={[
        {
          h: `How ${data.name} foreclosure actually works`,
          p: [
            `${data.name} uses a ${data.procedure.toLowerCase()} foreclosure procedure. The bank must issue a formal notice, and after the statutory ${data.noticeDays}-day period, a ${data.saleName.toLowerCase()} can be scheduled. In practice, from Notice of Default to actual sale runs ${data.timeToSale.toLowerCase()}.`,
            `${data.quickFact} Every case the Guild files in ${data.name} is anchored to that timeline — the counterclaim is prepared to be filed inside the statutory window, before the sale is finalized.`,
          ],
          internal: [
            { to: "/stop-foreclosure-fast", label: "The full 60-day timeline" },
            { to: "/sheriff-sale-help", label: "Emergency sheriff-sale help" },
          ],
        },
        {
          h: `What the Guild does in a ${data.abbrev} case, step by step`,
          p: [
            `Day 1–7 — intake, six signed agreements executed inside the private portal, and initial document upload (mortgage statement, deed, ID, ${data.abbrev} court paperwork). Day 8–20 — good accounting ledger audit on the loan file, including any SEC-bundled tranche the note was assigned into. Day 21–35 — the verified counterclaim is filed with the ${data.abbrev} court, the law firm on the pleadings, and the bank. Day 36–50 — foreclosure mediation and, if necessary, arbitration. Day 51–60 — trustee pledge, mortgage reassignment, settlement recording, and a manual tracking number stamped on the case file.`,
            `Every step is visible in the Member Portal in real time. Every filing is timestamped. Every trustee reply is preserved.`,
          ],
        },
        {
          h: `${data.name} counties we file in most`,
          p: [
            `Most of our ${data.abbrev} intake comes from ${countyList}. Every one of these county courts has heard a Guild counterclaim inside a live foreclosure case. Cases from smaller ${data.abbrev} counties are accepted without exception — timelines and procedures are identical.`,
            `If your case is in a county not listed here, you can still enroll. All ${data.name} counties are covered under the Guild's ${data.abbrev} program.`,
          ],
          internal: [
            { to: "/pricing", label: "See pricing" },
            { to: "/register", label: "Apply for membership" },
          ],
        },
        {
          h: `${data.name} sheriff-sale / trustee-sale emergency intake`,
          p: [
            `If a ${data.saleName.toLowerCase()} is already on the calendar in your ${data.name} case, message the trustee contact page immediately. Include the sale date, the property address, the case number, and the law firm name. Same-day trustee review is the standard on active sale intakes.`,
            `The counterclaim + injunctive relief request is typically respected by the ${data.abbrev} court and the sale is halted while the counterclaim is heard. Cases filed inside the last week require prioritized document upload and same-day agreement signing — the Guild has the emergency triage desk staffed to handle exactly this.`,
          ],
        },
      ]}
      faq={[
        {
          q: `Does the Guild work in every ${data.name} county?`,
          a: `Yes. Every ${data.abbrev} county is covered under the Guild's ${data.abbrev} program. The counterclaim is filed in whichever court is hearing your foreclosure — no county is excluded.`,
        },
        {
          q: `How long does a ${data.abbrev} foreclosure actually take?`,
          a: `${data.timeToSale}. ${data.quickFact}`,
        },
        {
          q: `Will filing a counterclaim stop my ${data.saleName.toLowerCase()}?`,
          a: `In the vast majority of qualified cases, yes. The counterclaim is heard by the ${data.abbrev} court and the sale is typically paused during that hearing. This is not a guaranteed outcome but it is the outcome in nearly every qualified case.`,
        },
        {
          q: `Do I need a ${data.name} attorney to enroll?`,
          a: `No. The Guild trustees prepare and file the counterclaim on your behalf. If your ${data.abbrev} case additionally requires a separate court appearance, we refer to a partner attorney licensed in ${data.name}.`,
        },
        {
          q: `Can I do this from my phone in ${data.name}?`,
          a: `Yes. The entire Guild experience — application, six signed agreements, document upload, trustee messaging — is fully mobile and tablet friendly. Most ${data.abbrev} members complete initial intake from their phone within the first day.`,
        },
        {
          q: `What does it cost in ${data.name}?`,
          a: `The same as everywhere else: $1,500 yearly Guild membership plus 1.5% of the mortgage balance as the counterclaim service fee, paid only at settlement. There is no ${data.abbrev} surcharge and no state-by-state pricing.`,
        },
      ]}
      ctaTitle={`Every day the ${data.saleName.toLowerCase()} clock in ${data.name} keeps running is a day lost.`}
    />
  );
}
